import { useRef, useState } from 'react';
import { CameraAltOutlined, Close } from '@mui/icons-material';
import { Box, Button, IconButton, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import { makeStyles } from 'tss-react/mui';
import { ImageVideoPickerDialog } from '../../shared/ImageVideoPickerDialog';

const useStyles = makeStyles()((theme) => {
    const { spacing } = theme;

    return {
        imgBtn: {
            border: 'dashed',
            borderWidth: 'thin',
            color: 'gray',
            width: '-webkit-fill-available',
            padding: spacing(1),
            textTransform: 'none'
        },
        boxImageContainer: {
            position: 'relative',
            width: 80,
            height: 80,
            borderRadius: 1,
            overflow: 'hidden'
        },
        imgPreview: {
            width: '100%',
            height: '100%',
            objectFit: 'cover'
        }
    };
});

interface FollowUpActionsGalleryProps {
    value: string[];
    onChange: (images: string[]) => void;
}

export const FollowUpActionsGallery = (props: FollowUpActionsGalleryProps) => {
    const { onChange, value } = props;
    const { classes } = useStyles();
    const { enqueueSnackbar } = useSnackbar();

    const [showImageDialog, setShowImageDialog] = useState(false);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const galleryInputRef = useRef<HTMLInputElement>(null);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            const readers = files.map((file) => {
                return new Promise<string>((resolve) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.readAsDataURL(file);
                });
            });

            Promise.all(readers)
                .then((results) => {
                    onChange([...value, ...results]);
                })
                .catch(() => {
                    enqueueSnackbar(JSON.stringify('Some images failed to load.'), {
                        variant: 'error'
                    });
                });
        }
    };

    const removeImage = (index: number) => {
        onChange(value.filter((_, i) => i !== index));
    };

    const handleOnSelect = async (option: string) => {
        setShowImageDialog(false);
        if (!option) return;

        if (option === 'browse') {
            galleryInputRef.current.click();
        }

        if (option === 'camera') {
            cameraInputRef.current.click();
        }
    };

    return (
        <>
            <div>
                <Button
                    className={classes.imgBtn}
                    onClick={() => setShowImageDialog(true)}
                    component="label"
                    startIcon={<CameraAltOutlined />}
                >
                    {_t('mobile_mobileform_add_follow_up_actions_gallery_add')}
                </Button>
                <input
                    hidden
                    type="file"
                    ref={cameraInputRef}
                    id="icon-button-photo"
                    capture="environment"
                    onChange={handleImageChange}
                />
                <input hidden type="file" ref={galleryInputRef} onChange={handleImageChange} />
                {value.length > 0 && (
                    <Stack direction="row" spacing={1} m={1} flexWrap="wrap">
                        {value.map((src, index) => (
                            <Box className={classes.boxImageContainer} key={index}>
                                <img className={classes.imgPreview} src={src} alt={`preview ${index}`} />
                                <IconButton
                                    size="small"
                                    onClick={() => removeImage(index)}
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        bgcolor: 'gray',
                                        color: 'white'
                                    }}
                                >
                                    <Close fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </Stack>
                )}
            </div>

            {showImageDialog && (
                <ImageVideoPickerDialog
                    title={_t('mobile_field_image_picker_dialog_title')}
                    onSelect={handleOnSelect}
                />
            )}
        </>
    );
};
